/**
 * Shared community data hook — business logic extracted from CommunityList.
 * Used by both desktop CommunityList and mobile MobileCommunityList.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import resolveAvatar from '../../../lib/resolveAvatar';
import { transformBackendPost, type FeedPost, type SortOption } from './useFeedData';

import { fetchWithAuth } from '../../../lib/fetchWithAuth';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Community = {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  memberCount: number;
  image?: string;
};

export type CommunityTab = 'joined' | 'popular' | 'friends' | 'following' | 'all';

// ---------------------------------------------------------------------------
// Sorting (community-specific: "popular" tab defaults to sort-by-likes)
// ---------------------------------------------------------------------------

function sortCommunityPosts(
  posts: FeedPost[],
  sortBy: SortOption,
  activeTab: CommunityTab,
): FeedPost[] {
  const sorted = [...posts];

  // When the "popular" tab is active and user hasn't explicitly changed sort,
  // default to sorting by likes.
  if (activeTab === 'popular' && sortBy === 'newest') {
    return sorted.sort((a, b) => b.likes - a.likes);
  }

  switch (sortBy) {
    case 'newest':
      return sorted.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    case 'oldest':
      return sorted.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    case 'popular':
      return sorted.sort((a, b) => b.likes - a.likes);
    case 'unpopular':
      return sorted.sort((a, b) => (b.dislikes || 0) - (a.dislikes || 0));
    case 'controversial':
      return sorted.sort((a, b) => {
        const aC = Math.min(a.likes, a.dislikes || 0);
        const bC = Math.min(b.likes, b.dislikes || 0);
        return bC - aC;
      });
    default:
      return sorted;
  }
}

// ---------------------------------------------------------------------------
// Transform a backend community post, resolving avatars
// ---------------------------------------------------------------------------

function transformCommunityPost(post: any): FeedPost {
  const base = transformBackendPost(post);
  return {
    ...base,
    authorAvatar: resolveAvatar(
      post.authorAvatar || post.author?.profilePicture || post.author?.image,
    ),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCommunityData() {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [friendsPosts, setFriendsPosts] = useState<FeedPost[]>([]);
  const [followingPosts, setFollowingPosts] = useState<FeedPost[]>([]);
  const [activeTab, setActiveTab] = useState<CommunityTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Fetch communities on mount ──
  useEffect(() => {
    (async () => {
      try {
        const response = await fetchWithAuth('/api/communities', { credentials: 'include' });
        if (response.ok && mountedRef.current) {
          const data = await response.json();
          const list: Community[] = (data.communities || []).map((c: any) => ({
            id: c.id,
            name: c.name,
            slug: c.nameSlug || c.slug,
            description: c.description,
            memberCount: c.memberCount ?? 0,
            image: c.avatar || c.image,
          }));
          setCommunities(list);
        }
      } catch {
        /* silent */
      }
    })();
  }, []);

  // ── Fetch posts based on active tab ──
  useEffect(() => {
    (async () => {
      try {
        // Main community posts (joined / popular / all)
        if (activeTab === 'joined' || activeTab === 'popular' || activeTab === 'all') {
          const res = await fetchWithAuth(`/api/communities/posts?tab=${activeTab}`, {
            credentials: 'include',
          });
          if (res.ok && mountedRef.current) {
            const data = await res.json();
            setPosts((data.posts || []).map(transformCommunityPost));
          }
        }

        // Friends activity
        if (activeTab === 'friends' || activeTab === 'all') {
          const res = await fetchWithAuth('/api/communities/friends/activity', {
            credentials: 'include',
          });
          if (res.ok && mountedRef.current) {
            const data = await res.json();
            setFriendsPosts((data.posts || []).map(transformCommunityPost));
          }
        }

        // Following activity
        if (activeTab === 'following' || activeTab === 'all') {
          const res = await fetchWithAuth('/api/communities/following/activity', {
            credentials: 'include',
          });
          if (res.ok && mountedRef.current) {
            const data = await res.json();
            setFollowingPosts((data.posts || []).map(transformCommunityPost));
          }
        }
      } catch {
        /* silent */
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    })();
  }, [activeTab]);

  // ── Filtered + sorted posts ──
  const getFilteredPosts = useCallback((): FeedPost[] => {
    switch (activeTab) {
      case 'joined':
      case 'popular':
        return posts;
      case 'friends':
        return friendsPosts;
      case 'following':
        return followingPosts;
      case 'all':
      default: {
        // Merge all sources, de-duplicate by id
        const merged = [...posts, ...friendsPosts, ...followingPosts];
        return merged.filter(
          (post, index, self) => index === self.findIndex((p) => p.id === post.id),
        );
      }
    }
  }, [activeTab, posts, friendsPosts, followingPosts]);

  const getSortedPosts = useCallback((): FeedPost[] => {
    return sortCommunityPosts(getFilteredPosts(), sortBy, activeTab);
  }, [getFilteredPosts, sortBy, activeTab]);

  // ── Refresh ──
  const refreshFeed = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await fetchWithAuth('/api/communities/posts', { credentials: 'include' });
      if (response.ok && mountedRef.current) {
        const data = await response.json();
        setPosts((data.posts || []).map(transformCommunityPost));
      }
    } catch {
      /* silent */
    } finally {
      if (mountedRef.current) {
        setTimeout(() => setIsRefreshing(false), 300);
      }
    }
  }, []);

  // ── Like post (optimistic update) ──
  const likePost = useCallback(async (postId: string) => {
    // Find across all lists
    const allPosts = [...posts, ...friendsPosts, ...followingPosts];
    const targetPost = allPosts.find((p) => p.id === postId) as any;
    if (!targetPost) return;

    const wasLiked = targetPost.liked;
    const initialLikes = targetPost.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Determine endpoint based on post type
    const communityId = targetPost.communityId;
    const endpoint =
      targetPost.postType === 'forum'
        ? `/api/communities/${communityId}/threads/${postId}/like`
        : `/api/communities/${communityId}/posts/${postId}/like`;

    const updateLike = (prev: FeedPost[]) =>
      prev.map((p) =>
        p.id === postId ? { ...p, liked: newLiked, likes: newLikes } : p,
      );

    const revertLike = (prev: FeedPost[]) =>
      prev.map((p) =>
        p.id === postId ? { ...p, liked: wasLiked, likes: initialLikes } : p,
      );

    setPosts(updateLike);
    setFriendsPosts(updateLike);
    setFollowingPosts(updateLike);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        setPosts(revertLike);
        setFriendsPosts(revertLike);
        setFollowingPosts(revertLike);
      }
    } catch {
      setPosts(revertLike);
      setFriendsPosts(revertLike);
      setFollowingPosts(revertLike);
    }
  }, [posts, friendsPosts, followingPosts]);

  // ── Dislike post (optimistic update) ──
  const dislikePost = useCallback(async (postId: string) => {
    const allPosts = [...posts, ...friendsPosts, ...followingPosts];
    const targetPost = allPosts.find((p) => p.id === postId) as any;
    if (!targetPost) return;

    const wasDisliked = targetPost.disliked;
    const initialDislikes = targetPost.dislikes || 0;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    const communityId = targetPost.communityId;
    const endpoint =
      targetPost.postType === 'forum'
        ? `/api/communities/${communityId}/threads/${postId}/dislike`
        : `/api/communities/${communityId}/posts/${postId}/dislike`;

    const updateDislike = (prev: FeedPost[]) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, disliked: newDisliked, dislikes: newDislikes }
          : p,
      );

    const revertDislike = (prev: FeedPost[]) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, disliked: wasDisliked, dislikes: initialDislikes }
          : p,
      );

    setPosts(updateDislike);
    setFriendsPosts(updateDislike);
    setFollowingPosts(updateDislike);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        setPosts(revertDislike);
        setFriendsPosts(revertDislike);
        setFollowingPosts(revertDislike);
      }
    } catch {
      setPosts(revertDislike);
      setFriendsPosts(revertDislike);
      setFollowingPosts(revertDislike);
    }
  }, [posts, friendsPosts, followingPosts]);

  // ── Delete post ──
  const deletePost = useCallback(async (postId: string) => {
    const allPosts = [...posts, ...friendsPosts, ...followingPosts];
    const targetPost = allPosts.find((p) => p.id === postId) as any;
    if (!targetPost) return;

    const communityId = targetPost.communityId;
    const endpoint = `/api/moderation/communities/${communityId}/posts/${postId}`;
    const removePost = (prev: FeedPost[]) => prev.filter((p) => p.id !== postId);

    try {
      const res = await fetch(endpoint, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setPosts(removePost);
        setFriendsPosts(removePost);
        setFollowingPosts(removePost);
      }
    } catch {
      /* silent */
    }
  }, [posts, friendsPosts, followingPosts]);

  return {
    communities,
    posts: getSortedPosts(),
    activeTab,
    setActiveTab,
    sortBy,
    setSortBy,
    isLoading,
    isRefreshing,
    refreshFeed,
    likePost,
    dislikePost,
    deletePost,
  };
}
